// Bluetooth thermal-printer transport. Holds the user's chosen default
// Bluetooth printer (by MAC address) and streams raw ESC/POS bytes to it over
// classic RFCOMM/SPP via the native BluetoothPrinterPlugin. Only available
// inside the Android app — browsers can't pair/stream classic Bluetooth.

import { isNativeApp, btEnsurePermissions, btPrint } from './nativeBluetooth';
import { cachedKey } from './idb';

export interface SavedBtPrinter {
  address: string;
  label?: string;
}

const btPrinterCache = cachedKey<SavedBtPrinter | null>('sconnect.btPrinter', null);

// Bluetooth printing is only possible inside the native Android shell.
export function isBtAvailable(): boolean {
  return isNativeApp();
}

export function getSavedBtPrinter(): SavedBtPrinter | null {
  return btPrinterCache.get();
}

export function saveBtPrinter(p: SavedBtPrinter): void {
  btPrinterCache.set(p);
}

export function clearBtPrinter(): void {
  btPrinterCache.clear();
}

// True when running natively and a default Bluetooth printer has been chosen.
export function btPrinterConfigured(): boolean {
  return isBtAvailable() && !!getSavedBtPrinter();
}

// Send raw bytes (ESC/POS) to the configured default Bluetooth printer.
export async function printBtBytes(data: Uint8Array): Promise<void> {
  const saved = getSavedBtPrinter();
  if (!saved) throw new Error('No Bluetooth printer selected');
  await printBtBytesTo(saved, data);
}

// Send raw bytes to a SPECIFIC saved Bluetooth printer.
export async function printBtBytesTo(printer: SavedBtPrinter, data: Uint8Array): Promise<void> {
  if (!isNativeApp()) throw new Error('Bluetooth printing is only available in the Android app');
  const granted = await btEnsurePermissions();
  if (!granted) throw new Error('Bluetooth permission denied');
  await btPrint(printer.address, data);
}
